import { useCallback, useMemo, useState } from "react";
import { useServiceResources, useServices } from "@/hooks/useApi";
import { PostgresCdcFields } from "./PostgresCdcFields";
import { derivedSlot, postgresCdcPayload } from "./postgresCdcPayload";
import type { SelectOption } from "./SelectDropdown";

/** The PostgreSQL services a change capture may read through. */
function usePostgresTargets() {
  const { data: services, isLoading } = useServices();
  const options = useMemo<SelectOption<string>[]>(
    () =>
      (services ?? [])
        .filter((s) => s.provider === "postgres")
        .map((s) => ({ value: s.name, label: s.name })),
    [services],
  );
  return { options, isLoading };
}

/** The tables of `targetService` a change capture may read. */
function useTargetTables(targetService: string) {
  const { data, isLoading } = useServiceResources(targetService);
  const options = useMemo<SelectOption<string>[]>(
    () =>
      (data?.resources ?? [])
        .filter((r) => r.resource_type === "table")
        .map((r) => ({ value: r.name, label: r.name })),
    [data],
  );
  return { options, isLoading: !!targetService && isLoading };
}

export function usePostgresCdcFields(serviceName = "") {
  const [targetService, setTargetService] = useState("");
  const [table, setTable] = useState("");
  const [heartbeat, setHeartbeat] = useState("");
  const targets = usePostgresTargets();
  const tables = useTargetTables(targetService);
  const slot = table ? derivedSlot(serviceName, table) : "";

  // Another service's tables are not this one's.
  const chooseTarget = useCallback((value: string) => {
    setTargetService(value);
    setTable("");
  }, []);

  const toPayload = useCallback(
    () =>
      postgresCdcPayload({
        name: serviceName,
        targetService,
        table,
        heartbeatIntervalSecs: heartbeat,
      }),
    [serviceName, targetService, table, heartbeat],
  );

  const fields = (
    <PostgresCdcFields
      targetService={targetService}
      setTargetService={chooseTarget}
      targetOptions={targets.options}
      targetsLoading={targets.isLoading}
      table={table}
      setTable={setTable}
      tableOptions={tables.options}
      tablesLoading={tables.isLoading}
      slot={slot}
      heartbeat={heartbeat}
      setHeartbeat={setHeartbeat}
    />
  );

  return { fields, toPayload };
}
